import type { GamificationEvent } from '../../types/auth';

/**
 * CORE-509 — what Agapella says when the Путь света moves.
 *
 * ## The one rule
 *
 * She phrases; she never states a right. «Тебе открылось создание сообщества»
 * is a product decision, and the front end is explicitly forbidden from holding
 * its own level→rights matrix (CORE-769 §0). What actually opened is computed
 * from the backend contract by `selectNewCapabilities` (CORE-774) and rendered
 * on the details screen — behind «Посмотреть», never in this bubble.
 *
 * So every line below is about the person and the stage, and nothing here can
 * promise a feature that doesn't exist.
 *
 * ## Why this file exists at all
 *
 * The decision is that the phrasing is model-generated, so this is the safe
 * fallback: used before her reply arrives, and instead of it when generation is
 * unavailable or the reply fails the constraints. A living voice is worth a lot,
 * but not at the cost of an invented right — or of an empty bubble.
 *
 * ## Tone
 *
 * No numbers, no praise, no comparison (see 09-light-path-companion.md §5), and
 * no gendered verb forms about the reader: the platform does not know whether
 * «ты вернулся» or «ты вернулась» is the true one, and guessing misgenders a
 * real person at the exact moment the product is trying to be warm. Present
 * tense usually solves it («ты возвращаешься»).
 *
 * A
 * stage is who you are becoming, not a prize; the copy stays true whether the
 * user is delighted or having a hard day, which is why the decision was to
 * adjust wording rather than withhold the event.
 */

export type CelebrationCopy = {
  text: string;
  primaryLabel: string;
  secondaryLabel: string;
};

/** Level code → Russian stage name, as the product names them. */
const STAGE_NAMES: Readonly<Record<string, string>> = {
  seeker: 'Искатель',
  keeper: 'Хранитель',
  // NOTE: `master` is the LEVEL «Проводник света», never the master ROLE. The
  // two must not be conflated anywhere the user can read it.
  master: 'Проводник света',
  grand_master: 'Магистр света',
};

/**
 * What each stage means, in her voice. One line each — the stages differ, and a
 * single template for all four would make every level-up sound the same.
 */
const STAGE_MEANING: Readonly<Record<string, string>> = {
  seeker: 'Ты в начале пути — самое время просто пробовать и смотреть, что откликается.',
  keeper: 'Это про устойчивость: практика перестаёт быть случайностью и становится частью дня.',
  master: 'Это про то, что рядом с тобой уже могут быть другие — ты держишь пространство не только для себя.',
  grand_master: 'Дальше идти некуда, и это тоже часть пути — теперь он про глубину, а не про движение вперёд.',
};

function stageName(event: GamificationEvent): string | null {
  const code = (event.referenceKey || '').trim();
  return STAGE_NAMES[code] ?? null;
}

export function celebrationCopy(event: GamificationEvent): CelebrationCopy {
  if (event.type === 'level_up') {
    const name = stageName(event);
    const meaning = STAGE_MEANING[(event.referenceKey || '').trim()] ?? '';
    // Without a recognised code we still have something true to say: the stage
    // changed. Better a plain sentence than a guessed name.
    const opening = name
      ? `Сегодня на твоём Пути света открылся новый этап — ${name}.`
      : 'Сегодня на твоём Пути света открылся новый этап.';
    return {
      text: [opening, meaning].filter(Boolean).join(' '),
      primaryLabel: 'Посмотреть',
      secondaryLabel: 'Спасибо, позже',
    };
  }

  if (event.type === 'achievement_earned') {
    // The name comes from the event (the backend's catalogue), never from here.
    const title = (event.message || '').replace(/[«»]/g, '').trim();
    return {
      text: title
        ? `У тебя новое достижение — «${title}». За такими вещами обычно стоит не один особенный день, а то, что ты возвращаешься к практике снова и снова.`
        : 'У тебя новое достижение. За такими вещами обычно стоит не один особенный день, а то, что ты возвращаешься к практике снова и снова.',
      primaryLabel: 'Посмотреть',
      secondaryLabel: 'Спасибо, позже',
    };
  }

  // milestone_reached — the streak crossed a mark. Deliberately no day count:
  // the details screen shows it, and repeating the number here turns constancy
  // back into a score.
  return {
    text: 'Твоя череда практик дошла до новой вехи. Это про тихую верность себе — возвращаться, даже когда день обычный.',
    primaryLabel: 'Посмотреть',
    secondaryLabel: 'Спасибо, позже',
  };
}
